/**
 * STYLE BUILDER (owner B2): the whole MapLibre style for a mood.
 *
 *   buildStyle(mood, { layout, contours, flags }) → StyleSpecification
 *   diffStyles(a, b)                              → paint/layout ops between two built styles (§E.8)
 *
 * Layer order (bottom → top), with the contract's empty anchors (contracts/map.ts ANCHORS):
 *   background
 *   ld-under-*               region pack at z ≥ 12, only visible where the detailed pack has no tile
 *   basemap earth / landcover / landuse / water
 *   ld-contours-minor, -major, ld-ridge, ld-contours-label    (hillshade is inserted beneath them at runtime)
 *   ── ld-anchor-terrain
 *   basemap roads, buildings, rail, boundaries, bridges
 *   ── ld-anchor-overlays     B5: veil, lit contours, site, rings, corridors
 *   ── ld-anchor-routes       B3: the LightLayer (+ fallback lines)
 *   ld-under-places_locality, basemap labels (§D.9 policy)
 *   ── ld-anchor-places       B5: dots, clusters, labels
 *   ── ld-anchor-top          B5: pin proxies
 *
 * No `sprite`. Glyphs are self-hosted. Sources: basemap (TileJSON on the in-memory archive), the
 * underlay (tile template, maxzoom 11), the contours (GeoJSON) and the anchors' empty source.
 */
import type { LayerSpecification, StyleSpecification } from "maplibre-gl";
import type { ContourCollection } from "@/contracts/data";
import { ANCHORS, SOURCE_IDS } from "@/contracts/map";
import type { LayerFlags, Layout, Mood } from "@/contracts/store";
import { BASEMAP_URL } from "@/map/memorySource";
import { LAYER_IDS } from "@/contracts/map";
import { contourLayers, contourSourceSpec } from "@/map/terrain";
import { UNDERLAY_PREFIX, UNDERLAY_SOURCE_ID, underlayLayers, underlaySourceSpec } from "@/map/underlay";
import { basemapLayers, isKeptLabel, protomapsIdOf } from "./flavors";
import { MOOD_TRANSITION_MS } from "./moods";

export const BASEMAP_ATTRIBUTION = "© OpenStreetMap contributors · Protomaps";
/** Anchor ids in z-order, bottom → top. */
export const ANCHOR_ORDER = [ANCHORS.terrain, ANCHORS.overlays, ANCHORS.routes, ANCHORS.places, ANCHORS.top] as const;

export interface BuildOptions {
  layout?: Layout;
  /** geo.contours from the store; omitted = an empty collection (tests, or before data). */
  contours?: ContourCollection | null;
  /** Layer flags baked into the style (label and contour visibility). Default: all on. */
  flags?: Partial<Pick<LayerFlags, "labels" | "contours">>;
  /** Glyph URL template; default `${location.origin}/glyphs/{fontstack}/{range}.pbf`. */
  glyphs?: string;
}

export function glyphsUrl(): string {
  const origin = typeof location !== "undefined" && location.origin && location.origin !== "null" ? location.origin : "";
  return `${origin}/glyphs/{fontstack}/{range}.pbf`;
}

function anchorLayer(id: string): LayerSpecification {
  return { id, type: "line", source: SOURCE_IDS.anchors, layout: { visibility: "none" } };
}

type AnyLayer = LayerSpecification & { layout?: Record<string, unknown>; paint?: Record<string, unknown> };

/** A kept basemap label layer with the labels flag applied (text-opacity + visibility). */
function withLabelFlag(l: LayerSpecification, visible: boolean): LayerSpecification {
  const a = l as AnyLayer;
  return {
    ...a,
    layout: { ...(a.layout ?? {}), visibility: visible ? "visible" : "none" },
    paint: { ...(a.paint ?? {}), "text-opacity": visible ? 1 : 0 },
  } as LayerSpecification;
}

export function buildStyle(mood: Mood, o: BuildOptions = {}): StyleSpecification {
  const layout = o.layout;
  const labelsOn = o.flags?.labels !== false;
  const contoursOn = o.flags?.contours !== false;

  const base = basemapLayers(mood, SOURCE_IDS.basemap, { layout });
  if (base[0]?.type !== "background") throw new Error("buildStyle: the flavor no longer starts with its background layer");
  const firstRoad = base.findIndex((l) => l.id.startsWith("roads_tunnels"));
  const firstLabel = base.findIndex((l) => l.type === "symbol");
  if (firstRoad < 0 || firstLabel < 0 || firstLabel < firstRoad) throw new Error("buildStyle: unexpected Protomaps layer order");

  const [background, ...rest] = base;
  const fills = rest.slice(0, firstRoad - 1);
  const roads = base.slice(firstRoad, firstLabel);
  const labels = base.slice(firstLabel).map((l) => withLabelFlag(l, labelsOn));
  const under = underlayLayers(mood, { layout });

  const layers: LayerSpecification[] = [
    background,
    ...under.base,
    ...fills,
    ...contourLayers(mood, { layout, visible: contoursOn }),
    anchorLayer(ANCHORS.terrain),
    ...roads,
    anchorLayer(ANCHORS.overlays),
    anchorLayer(ANCHORS.routes),
    ...under.labels.map((l) => withLabelFlag(l, labelsOn)),
    ...labels,
    anchorLayer(ANCHORS.places),
    anchorLayer(ANCHORS.top),
  ];

  return {
    version: 8,
    name: `ladera-${mood}`,
    glyphs: o.glyphs ?? glyphsUrl(),
    // The style's own transition is the mood change (§E.8); anything else sets its `-transition` explicitly.
    transition: { duration: MOOD_TRANSITION_MS, delay: 0 },
    projection: { type: "mercator" },
    sources: {
      [SOURCE_IDS.basemap]: { type: "vector", url: BASEMAP_URL, attribution: BASEMAP_ATTRIBUTION },
      [UNDERLAY_SOURCE_ID]: underlaySourceSpec(),
      [SOURCE_IDS.contours]: contourSourceSpec(o.contours),
      [SOURCE_IDS.anchors]: { type: "geojson", data: { type: "FeatureCollection", features: [] } },
    },
    layers,
  };
}

/** Ids of the basemap label layers the `labels` flag controls (basemap + underlay; not contour labels). */
export function labelLayerIds(style: StyleSpecification): string[] {
  return style.layers
    .filter((l) => l.type === "symbol" && l.id !== LAYER_IDS.contoursLabel && isKeptLabel(protomapsIdOf(l.id, UNDERLAY_PREFIX)))
    .map((l) => l.id);
}

/* ─── Diff (mood changes are paint-only, §E.8) ──────────────────────────── */

export interface StyleOp {
  kind: "paint" | "layout" | "filter" | "zoom";
  layer: string;
  key?: string;
  value: unknown;
}
export interface StyleDiff {
  ops: StyleOp[];
  /** Anything that would need a layer or source added, removed, reordered or retyped. Must be empty. */
  structural: string[];
}

const same = (x: unknown, y: unknown): boolean => x === y || JSON.stringify(x) === JSON.stringify(y);

/**
 * Compares two built styles layer by layer. Sources are compared by id and type only (their data is
 * shared and large). A mood change must yield `structural.length === 0` and only paint ops.
 */
export function diffStyles(a: StyleSpecification, b: StyleSpecification): StyleDiff {
  const structural: string[] = [];
  const ops: StyleOp[] = [];
  const sa = Object.keys(a.sources).sort();
  const sb = Object.keys(b.sources).sort();
  if (!same(sa, sb)) structural.push(`sources differ: [${sa.join(",")}] vs [${sb.join(",")}]`);
  for (const id of sa) if (b.sources[id] && a.sources[id].type !== b.sources[id].type) structural.push(`source ${id} changes type`);

  if (a.layers.length !== b.layers.length) structural.push(`layer count ${a.layers.length} → ${b.layers.length}`);
  const n = Math.min(a.layers.length, b.layers.length);
  for (let i = 0; i < n; i++) {
    const la = a.layers[i] as AnyLayer & { source?: string; "source-layer"?: string; filter?: unknown };
    const lb = b.layers[i] as AnyLayer & { source?: string; "source-layer"?: string; filter?: unknown };
    if (la.id !== lb.id) {
      structural.push(`layer ${i}: ${la.id} → ${lb.id}`);
      continue;
    }
    if (la.type !== lb.type || la.source !== lb.source || la["source-layer"] !== lb["source-layer"]) {
      structural.push(`layer ${la.id} changes type or source`);
      continue;
    }
    const pa = la.paint ?? {};
    const pb = lb.paint ?? {};
    for (const k of new Set([...Object.keys(pa), ...Object.keys(pb)])) {
      if (!same(pa[k], pb[k])) ops.push({ kind: "paint", layer: la.id, key: k, value: pb[k] });
    }
    const ya = la.layout ?? {};
    const yb = lb.layout ?? {};
    for (const k of new Set([...Object.keys(ya), ...Object.keys(yb)])) {
      if (!same(ya[k], yb[k])) ops.push({ kind: "layout", layer: la.id, key: k, value: yb[k] });
    }
    if (!same(la.filter, lb.filter)) ops.push({ kind: "filter", layer: la.id, value: lb.filter ?? null });
    if (la.minzoom !== lb.minzoom || la.maxzoom !== lb.maxzoom) ops.push({ kind: "zoom", layer: la.id, value: [lb.minzoom, lb.maxzoom] });
  }
  return { ops, structural };
}

/** Paint properties that accept a `-transition` (colours, opacities, widths, blurs, offsets, exaggeration). */
export function isTransitionable(key: string): boolean {
  return /-(color|opacity|width|blur|exaggeration|offset|translate|radius)$/.test(key) && !key.endsWith("-transition");
}
